import * as XLSX from 'xlsx';

export type WbsImportValidationOptions = {
    validateWeight: boolean;
    validateDates: boolean;
};

export type WbsImportIssue = {
    level: 'ERROR' | 'WARNING';
    rowNo?: number;
    code?: string;
    field?: string;
    message: string;
};

export type WbsImportRow = {
    rowNo: number;
    code: string;
    name: string;
    weight: number | null;
    plannedStart: string | null;
    plannedFinish: string | null;
    days: number | null;
};

export type SaveDraftItemInput = {
    clientId: string;
    parentClientId?: string;
    orderIndex: number;
    name: string;
    plannedStart?: string | null;
    plannedFinish?: string | null;
    weight?: number | null;
    memo?: string | null;
};

export type WbsImportPreview = {
    rows: WbsImportRow[];
    issues: WbsImportIssue[];
    items: SaveDraftItemInput[];
};

const CODE_RE = /^\d+(\.\d+)*$/;
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

function toTrimmedString(v: unknown): string {
    return String(v ?? '').trim();
}

function toNullableNumber(v: unknown): number | null {
    const s = String(v ?? '').trim();
    if (!s) return null;
    const n = Number(s);
    return Number.isFinite(n) ? n : null;
}

function getParentCode(code: string): string | null {
    const parts = code.split('.');
    if (parts.length <= 1) return null;
    return parts.slice(0, -1).join('.');
}

function getOrderIndex(code: string): number {
    const last = Number(code.split('.').pop() ?? '1');
    return (Number.isFinite(last) && last > 0 ? last : 1) * 10;
}

function pick(obj: Record<string, unknown>, keys: string[]) {
    for (const k of keys) {
        if (k in obj) return obj[k];
    }
    return undefined;
}

function normalizeHeaderCell(v: unknown): string {
    return String(v ?? '')
        .trim()
        .replace(/\s+/g, ' ')
        .toLowerCase();
}

function findHeaderRowIndex(rows: unknown[][]): number {
    for (let i = 0; i < Math.min(rows.length, 20); i++) {
        const headerSet = new Set((rows[i] ?? []).map(normalizeHeaderCell));

        const hasCode = headerSet.has('code') || headerSet.has('계층코드');
        const hasName = headerSet.has('wbs name') || headerSet.has('name') || headerSet.has('작업명');
        const hasWeight = headerSet.has('weight');

        if (hasCode && hasName && hasWeight) {
            return i;
        }
    }
    return -1;
}

function calcInclusiveDays(start?: string | null, finish?: string | null): number | null {
    if (!start || !finish) return null;
    if (!DATE_RE.test(start) || !DATE_RE.test(finish)) return null;

    const [sy, sm, sd] = start.split('-').map(Number);
    const [fy, fm, fd] = finish.split('-').map(Number);

    const s = Date.UTC(sy, sm - 1, sd);
    const f = Date.UTC(fy, fm - 1, fd);

    const ms = f - s;
    const days = Math.floor(ms / 86400000) + 1;
    return days >= 1 ? days : null;
}

export async function parseWbsImportFile(
    file: File,
    options: WbsImportValidationOptions = {
        validateWeight: true,
        validateDates: true,
    },
): Promise<WbsImportPreview> {
    const buf = await file.arrayBuffer();
    const wb = XLSX.read(buf, { type: 'array' });
    const ws = wb.Sheets[wb.SheetNames[0]];
    if (!ws) throw new Error('첫 번째 시트를 읽을 수 없습니다.');

    const matrix = XLSX.utils.sheet_to_json<unknown[]>(ws, {
        header: 1,
        defval: '',
        raw: false,
    });

    const headerRowIndex = findHeaderRowIndex(matrix);
    if (headerRowIndex < 0) {
        throw new Error('헤더 행을 찾을 수 없습니다. Code / Wbs Name / Weight 컬럼이 필요합니다.');
    }

    const headers = (matrix[headerRowIndex] ?? []).map((v) => String(v ?? '').trim());
    const dataRows = matrix.slice(headerRowIndex + 1);

    const rawRows: Record<string, unknown>[] = dataRows.map((row) => {
        const obj: Record<string, unknown> = {};
        headers.forEach((h, idx) => {
            obj[h] = row[idx];
        });
        return obj;
    });

    const rows: WbsImportRow[] = rawRows
        .map((r, idx) => {
            const rowNo = headerRowIndex + 2 + idx;
            const code = toTrimmedString(pick(r, ['Code', 'code', '계층코드']));
            const name = toTrimmedString(pick(r, ['Wbs Name', 'WBS Name', 'Name', '작업명']));
            const weight = toNullableNumber(pick(r, ['Weight', 'weight', '가중치']));
            const plannedStart = toTrimmedString(pick(r, ['Start', 'start', '시작', '시작일'])) || null;
            const plannedFinish = toTrimmedString(pick(r, ['Finish', 'finish', '종료', '종료일'])) || null;

            const days = options.validateDates ? calcInclusiveDays(plannedStart, plannedFinish) : null;

            return {
                rowNo,
                code,
                name,
                weight,
                plannedStart,
                plannedFinish,
                days,
            };
        })
        .filter((r) => r.code || r.name || r.weight != null || r.plannedStart || r.plannedFinish);

    const issues: WbsImportIssue[] = [];

    // 1) 기본 필수 검증: Code / Name
    for (const row of rows) {
        if (!row.code) {
            issues.push({
                level: 'ERROR',
                rowNo: row.rowNo,
                field: 'Code',
                message: 'Code가 비어 있습니다.',
            });
            continue;
        }

        if (!CODE_RE.test(row.code)) {
            issues.push({
                level: 'ERROR',
                rowNo: row.rowNo,
                code: row.code,
                field: 'Code',
                message: 'Code 형식이 올바르지 않습니다. 예: 1 / 1.2 / 1.2.3',
            });
        }

        if (!row.name) {
            issues.push({
                level: 'ERROR',
                rowNo: row.rowNo,
                code: row.code,
                field: 'Wbs Name',
                message: 'WBS Name이 비어 있습니다.',
            });
        }
    }

    // 2) 중복 Code 검증
    const seen = new Set<string>();
    for (const row of rows) {
        if (!row.code || !CODE_RE.test(row.code)) continue;

        if (seen.has(row.code)) {
            issues.push({
                level: 'ERROR',
                rowNo: row.rowNo,
                code: row.code,
                field: 'Code',
                message: '중복된 Code입니다.',
            });
            continue;
        }
        seen.add(row.code);
    }

    // 3) Code segment 검증
    for (const row of rows) {
        if (!row.code || !CODE_RE.test(row.code)) continue;

        const rawSegs = row.code.split('.');
        const numSegs = rawSegs.map(Number);

        if (numSegs.some((n) => !Number.isInteger(n) || n <= 0)) {
            issues.push({
                level: 'ERROR',
                rowNo: row.rowNo,
                code: row.code,
                field: 'Code',
                message: 'Code의 각 segment는 1 이상의 정수여야 합니다. 예: 1 / 1.2 / 1.2.3',
            });
            continue;
        }

        if (rawSegs.some((seg) => seg.length > 1 && seg.startsWith('0'))) {
            issues.push({
                level: 'ERROR',
                rowNo: row.rowNo,
                code: row.code,
                field: 'Code',
                message: 'Code segment에 앞자리 0을 사용할 수 없습니다. 예: 01, 1.02 금지',
            });
        }
    }

    // 4) 부모 Code 존재 검증
    const byCode = new Map(rows.map((r) => [r.code, r]));
    for (const row of rows) {
        if (!row.code || !CODE_RE.test(row.code)) continue;
        const parentCode = getParentCode(row.code);
        if (parentCode && !byCode.has(parentCode)) {
            issues.push({
                level: 'ERROR',
                rowNo: row.rowNo,
                code: row.code,
                field: 'Code',
                message: `상위 Code(${parentCode})가 파일 안에 없습니다.`,
            });
        }
    }

    // 5) Weight 검증
    if (options.validateWeight) {
        let hasMissingWeight = false;

        for (const row of rows) {
            if (row.weight == null) {
                hasMissingWeight = true;
                issues.push({
                    level: 'ERROR',
                    rowNo: row.rowNo,
                    code: row.code,
                    field: 'Weight',
                    message: '가중치 검증이 선택된 경우 Weight는 필수입니다.',
                });
            }
        }

        if (!hasMissingWeight) {
            for (const row of rows) {
                if (row.weight != null && (Number.isNaN(row.weight) || row.weight <= 0 || row.weight > 100)) {
                    issues.push({
                        level: 'ERROR',
                        rowNo: row.rowNo,
                        code: row.code,
                        field: 'Weight',
                        message: 'Weight는 0보다 크고 100이하여야 합니다.',
                    });
                }
            }

            const childrenByParent = new Map<string, WbsImportRow[]>();
            for (const row of rows) {
                const parentCode = getParentCode(row.code);
                if (!parentCode) continue;
                const arr = childrenByParent.get(parentCode) ?? [];
                arr.push(row);
                childrenByParent.set(parentCode, arr);
            }

            for (const [parentCode, children] of childrenByParent.entries()) {
                const parent = byCode.get(parentCode);
                if (!parent || parent.weight == null) continue;

                const childWeights = children.map((c) => c.weight).filter((v): v is number => v != null);

                if (childWeights.length !== children.length) continue;

                const sum = childWeights.reduce((a, b) => a + b, 0);
                if (Math.abs(sum - parent.weight) > 0.0001) {
                    issues.push({
                        level: 'WARNING',
                        rowNo: parent.rowNo,
                        code: parent.code,
                        field: 'Weight',
                        message: `자식 Weight 합(${sum})이 부모 Weight(${parent.weight})와 다릅니다.`,
                    });
                }
            }
        }
    }

    // 6) Date 검증
    if (options.validateDates) {
        let hasMissingDates = false;

        for (const row of rows) {
            if (!row.plannedStart) {
                hasMissingDates = true;
                issues.push({
                    level: 'ERROR',
                    rowNo: row.rowNo,
                    code: row.code,
                    field: 'Start',
                    message: '날짜 검증이 선택된 경우 Start는 필수입니다.',
                });
            }

            if (!row.plannedFinish) {
                hasMissingDates = true;
                issues.push({
                    level: 'ERROR',
                    rowNo: row.rowNo,
                    code: row.code,
                    field: 'Finish',
                    message: '날짜 검증이 선택된 경우 Finish는 필수입니다.',
                });
            }
        }

        if (!hasMissingDates) {
            for (const row of rows) {
                if (row.plannedStart && !DATE_RE.test(row.plannedStart)) {
                    issues.push({
                        level: 'ERROR',
                        rowNo: row.rowNo,
                        code: row.code,
                        field: 'Start',
                        message: 'Start 날짜 형식이 올바르지 않습니다. 예: 2026-01-29',
                    });
                }

                if (row.plannedFinish && !DATE_RE.test(row.plannedFinish)) {
                    issues.push({
                        level: 'ERROR',
                        rowNo: row.rowNo,
                        code: row.code,
                        field: 'Finish',
                        message: 'Finish 날짜 형식이 올바르지 않습니다. 예: 2026-08-14',
                    });
                }

                if (row.plannedStart && row.plannedFinish && row.plannedFinish < row.plannedStart) {
                    issues.push({
                        level: 'ERROR',
                        rowNo: row.rowNo,
                        code: row.code,
                        field: 'Finish',
                        message: 'Finish는 Start보다 빠를 수 없습니다.',
                    });
                }
            }

            for (const row of rows) {
                const parentCode = getParentCode(row.code);
                if (!parentCode) continue;

                const parent = byCode.get(parentCode);
                if (!parent) continue;

                if (parent.plannedStart && row.plannedStart && row.plannedStart < parent.plannedStart) {
                    issues.push({
                        level: 'WARNING',
                        rowNo: row.rowNo,
                        code: row.code,
                        field: 'Start',
                        message: `자식 Start(${row.plannedStart})가 부모 Start(${parent.plannedStart})보다 빠릅니다.`,
                    });
                }

                if (parent.plannedFinish && row.plannedFinish && row.plannedFinish > parent.plannedFinish) {
                    issues.push({
                        level: 'WARNING',
                        rowNo: row.rowNo,
                        code: row.code,
                        field: 'Finish',
                        message: `자식 Finish(${row.plannedFinish})가 부모 Finish(${parent.plannedFinish})보다 늦습니다.`,
                    });
                }
            }
        }
    }

    const hasError = (row: WbsImportRow) => issues.some((x) => x.level === 'ERROR' && x.rowNo === row.rowNo);

    const sortedRows = [...rows]
        .filter((r) => !hasError(r))
        .sort((a, b) => {
            const aa = a.code.split('.').map(Number);
            const bb = b.code.split('.').map(Number);
            const len = Math.max(aa.length, bb.length);
            for (let i = 0; i < len; i++) {
                const av = aa[i] ?? -1;
                const bv = bb[i] ?? -1;
                if (av !== bv) return av - bv;
            }
            return 0;
        });

    const items: SaveDraftItemInput[] = sortedRows.map((row) => {
        const parentCode = getParentCode(row.code);

        return {
            clientId: row.code,
            ...(parentCode ? { parentClientId: parentCode } : {}),
            orderIndex: getOrderIndex(row.code),
            name: row.name,
            plannedStart: row.plannedStart,
            plannedFinish: row.plannedFinish,
            weight: row.weight,
            memo: null,
        };
    });

    return {
        rows,
        issues,
        items,
    };
}
